/**
 * @fileoverview Left-sidebar navigation for the redesigned instructor portal
 * ("Studio Quiet"). Brand block + the single global Record CTA + primary nav +
 * Settings pinned to the bottom. Shared by the fixed desktop rail and the
 * mobile drawer (see StudioLayout).
 */

import { useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "@/App";
import { displayName } from "@/lib/userName";
import {
  BILLING_ENABLED, MARKETPLACE_ENABLED, SCHEDULING_ENABLED, SCHEDULER_VIEW_ENABLED, invoiceHistoryEnabled,
} from "@/lib/featureFlags";
import { accountFeatureEnabled } from "@/lib/orgFeatures";
import { programsPath, programsBadgeCount } from "@/lib/programsRoute";
import { Icon } from "@/components/studio/primitives";
import FeedbackButton from "@/components/FeedbackButton";

// `feature` marks a nav item hidden when an org turns that feature off. ONLY
// account-wide toggles may appear here — a per-context toggle (scheduling,
// billing) governs just the writes attributed to that org's program, so the
// feature keeps working personally and its nav must stay visible.
const PRIMARY_NAV = [
  { id: "dashboard", label: "Home", icon: "home", path: "/dashboard" },
  { id: "students", label: "Students", icon: "users", path: "/students" },
  // Lessons sits directly under Students because it answers the other half
  // of the same question — who you teach, and what you have taught them.
  // It used to be reachable only from a link on the dashboard, which made
  // the whole teaching history a place you had to already know about.
  { id: "lessons", label: "Lessons", icon: "history", path: "/all-lessons" },
  // Programs (was "Marketplace"). No `feature` key: the org toggle that used to
  // hide this is retired, and the tab now also holds the programs the
  // instructor already teaches in — which no org may hide from them.
  ...(MARKETPLACE_ENABLED ? [{ id: "programs", label: "Programs", icon: "grid", path: null }] : []),
  // Two surfaces, one slot, decided by the cutover flag. Before it, "Schedule"
  // AUTHORS Hans-native slots. After it, Scheduler owns the schedule and
  // "Calendar" mirrors it — so the pre-cutover page must not survive the flip,
  // and the tab must not vanish with it (which is what happened: the item was
  // gated off and nothing took its place, leaving the calendar reachable only
  // through a settings deep link).
  //
  // No tier gate on either. `studio_layer` gates authorship and never reads.
  ...(SCHEDULING_ENABLED
    ? [
        SCHEDULER_VIEW_ENABLED
          ? { id: "calendar", label: "Calendar", icon: "calendar", path: "/calendar" }
          : { id: "schedule", label: "Schedule", icon: "calendar", path: "/schedule" },
      ]
    : []),
  // Also behind the server's INSTRUCTOR_INVOICE_HISTORY_ENABLED (`runtime`
  // below): the retained history of retired tuition invoicing, off by default.
  ...(BILLING_ENABLED
    ? [{ id: "billing", label: "Invoice history", icon: "receipt", path: "/billing", runtime: invoiceHistoryEnabled }]
    : []),
  { id: "repertoires", label: "Repertoires", icon: "book", path: "/repertoire", feature: "repertoire" },
  { id: "tools", label: "Tools", icon: "metronome", path: "/tools" },
];

const SETTINGS_NAV = { id: "settings", label: "Settings", icon: "settings", path: "/settings" };

/** Which nav item owns the current path (mirrors handoff active-state rules). */
export function activeNavId(pathname) {
  if (pathname.startsWith("/calendar")) return "calendar";
  if (pathname.startsWith("/schedule")) return "schedule";
  if (pathname.startsWith("/billing")) return "billing";
  if (pathname.startsWith("/marketplace")) return "programs"; // legacy path, redirected
  if (/^\/instructors\/[^/]+\/programs/.test(pathname)) return "programs";
  if (pathname.startsWith("/students") || pathname.startsWith("/classes")) return "students";
  if (pathname.startsWith("/repertoire")) return "repertoires";
  if (pathname.startsWith("/tools") || pathname === "/metronome" || pathname === "/tuner") return "tools";
  if (pathname.startsWith("/settings") || pathname.startsWith("/consent-ledger")) return "settings";
  // /all-lessons has its own nav item now — it used to fall through to Home.
  if (pathname.startsWith("/all-lessons")) return "lessons";
  // Home stays active on the lesson-summary route: a single lesson is opened
  // from several places, so no one nav item owns it.
  if (pathname.startsWith("/lesson") || pathname.startsWith("/dashboard")) return "dashboard";
  return "dashboard";
}

function NavItem({ item, active, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item.path)}
      aria-current={active ? "page" : undefined}
      data-testid={`nav-${item.id}`}
      className={[
        "flex items-center gap-[14px] rounded-[12px] border px-[14px] py-[11px] text-left transition-colors duration-150",
        active
          ? "border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)]"
          : "border-transparent hover:bg-[var(--studio-raised)]",
      ].join(" ")}
    >
      <Icon
        name={item.icon}
        size={20}
        strokeWidth={active ? 2 : 1.7}
        className={active ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-secondary)]"}
      />
      <span
        className={[
          "flex-1 font-sans text-base",
          active ? "font-semibold text-[var(--text-primary)]" : "font-medium text-[var(--text-secondary)]",
        ].join(" ")}
      >
        {item.label}
      </span>
      {/* A waiting invitation outranks the active-page marker: the count is the
          only thing on the page that says something is waiting for you, and
          being ON Programs doesn't answer it.

          Capped at 9+ so the badge can't stretch the row and push the label —
          past a certain point the exact number stops changing what you do. */}
      {item.count ? (
        <span
          data-testid={`nav-count-${item.id}`}
          aria-label={`${item.count} waiting`}
          className="min-w-[20px] shrink-0 rounded-full bg-[var(--state-success)] px-1.5 py-[3px] text-center font-mono text-[11px] font-semibold leading-none text-[var(--text-on-state)]"
        >
          {item.count > 9 ? "9+" : item.count}
        </span>
      ) : (
        active && <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent-yellow)]" />
      )}
    </button>
  );
}

/**
 * The sidebar's inner content. `onNavigate` fires after any navigation so the
 * mobile drawer can close itself.
 */
export function SidebarInner({ onNavigate }) {
  const { user } = useContext(AuthContext) || {};
  const navigate = useNavigate();
  const location = useLocation();
  const active = activeNavId(location.pathname);

  const go = (path) => {
    navigate(path);
    onNavigate?.();
  };

  // Multi-org (Spec 12): the shell stays vanilla Hans — an instructor can belong
  // to several orgs, so there's no single account-wide co-brand, and program
  // co-branding is per-lesson.
  //
  // Nav hiding is the narrow exception, same reasoning as the add-student
  // affordance: co-brand can't be resolved across several memberships, but a
  // *policy* answer can (disabled by ANY org ⇒ disabled), and a nav item that
  // leads only to a 403 is worse than no nav item. Cosmetic only — the server
  // 403 (feature_disabled) remains the authoritative gate, which is why this
  // covers account-wide toggles exclusively.
  const nav = PRIMARY_NAV.filter(
    (item) => (!item.feature || accountFeatureEnabled(user, item.feature)) && (!item.runtime || item.runtime(user)),
  ).map((item) =>
    // Programs is the one user-scoped path in the nav (see lib/programsRoute).
    // Three things live behind Programs and all three ask to be answered: a
    // program invitation, an organization invitation, and a change an org made
    // to a program this instructor teaches in. One number, because the nav
    // item is one destination — the tab inside says which is which.
    item.id === "programs"
      ? { ...item, path: programsPath(user), count: programsBadgeCount(user) }
      : item,
  );

  return (
    <div className="flex h-full flex-col px-4 pb-5 pt-[26px]">
      {/* Brand */}
      <div className="flex items-center gap-3 px-2 pb-[22px]">
        <div className="flex h-[42px] w-[42px] items-center justify-center overflow-hidden rounded-[12px] border border-[var(--studio-divider-strong)] bg-black">{/* brand-lint-ignore: true-black logo tile, matches StudentPortalSidebar + the logo PNG's black backdrop */}
          <img src="/logo192.png" alt="Hans" className="h-full w-full object-cover" />
        </div>
        <div className="min-w-0">
          <div className="truncate font-sans text-lg font-bold leading-none text-[var(--text-primary)]">Hans</div>
          <div className="mt-[3px] truncate font-sans text-[13px] text-[var(--text-tertiary)]">
            {displayName(user) || "Instructor"}
          </div>
        </div>
      </div>

      {/* Record — the single, signature yellow gesture */}
      <button
        type="button"
        onClick={() => go("/hans")}
        data-testid="sidebar-record"
        className="mb-[22px] flex items-center justify-center gap-[10px] rounded-[14px] bg-[var(--accent-yellow)] px-4 py-[14px] font-sans text-base font-bold text-[var(--text-inverse)] shadow-[0_6px_24px_var(--accent-yellow-glow)] transition-transform duration-150 hover:-translate-y-px active:scale-[0.98]"
      >
        <Icon name="mic" size={19} strokeWidth={2.2} className="text-[var(--text-inverse)]" />
        Record lesson
      </button>

      {/* Primary nav */}
      <nav className="flex flex-col gap-1">
        {nav.map((item) => (
          <NavItem key={item.id} item={item} active={active === item.id} onSelect={go} />
        ))}
      </nav>

      <div className="flex-1" />

      {/* Settings pinned to the bottom, with the founding cohort's persistent
          Share-feedback control above it. Renders nothing for everyone else —
          the component decides, so the sidebar stays ignorant of who is in the
          cohort. It sits here rather than in the nav list because it is not a
          destination: it opens a dialog over whatever you were doing, which
          matters most on the record screen, where navigating away would end
          the recording. */}
      <div className="border-t border-[var(--studio-divider)] pt-3">
        <FeedbackButton onDone={onNavigate} />
        <NavItem item={SETTINGS_NAV} active={active === "settings"} onSelect={go} />
      </div>
    </div>
  );
}

export default SidebarInner;
